import * as THREE from 'three';
import { WallJointCache, WallJointKey } from '../types/wallCacheTypes';
import { WallType, WallShapeType } from '../types/floorplannerTypes';
import { WallChangeDetector } from './wallChangeDetection';

export class WallJointCacheManager {
  private cache = new Map<string, WallJointCache>();
  private maxCacheSize = 1000;
  private cacheHits = 0;
  private cacheMisses = 0;

  /**
   * Generate a unique key for a wall joint based on connected walls
   */
  static generateJointKey(wallId: string, connectedWallIds: string[], positionHash: string): string {
    const sortedIds = [...connectedWallIds].sort().join(',');
    return `${wallId}:${sortedIds}:${positionHash}`;
  }

  /**
   * Create a joint cache entry
   */
  createJointCache(
    wallId: string,
    connectedWallIds: string[],
    wallPositions: Map<string, { start: THREE.Vector2; end: THREE.Vector2; wallWidth: number }>,
    computedShapes: {
      inner: WallShapeType[];
      outline1: WallShapeType[];
      outline2: WallShapeType[];
      endCaps: WallShapeType[];
    }
  ): WallJointCache {
    const positionHash = Array.from(wallPositions.values())
      .map(pos => `${pos.start.x.toFixed(3)},${pos.start.y.toFixed(3)}|${pos.end.x.toFixed(3)},${pos.end.y.toFixed(3)}`)
      .join('|');

    const jointKey = WallJointCacheManager.generateJointKey(wallId, connectedWallIds, positionHash);
    const connectionHash = Array.from(wallPositions.keys())
      .sort()
      .map(id => {
        const pos = wallPositions.get(id);
        return pos ? `${id}:${pos.wallWidth}` : id;
      })
      .join('|');

    const cache: WallJointCache = {
      jointKey,
      wallPositions: new Map(wallPositions),
      computedShapes: {
        inner: computedShapes.inner.map(shape => ({...shape})),
        outline1: computedShapes.outline1.map(shape => ({...shape})),
        outline2: computedShapes.outline2.map(shape => ({...shape})),
        endCaps: computedShapes.endCaps.map(shape => ({...shape}))
      },
      lastModified: Date.now(),
      dependencies: new Set(connectedWallIds),
      connectionHash
    };

    this.cache.set(jointKey, cache);
    this.cleanupCache();

    return cache;
  }

  /**
   * Get cached joint if it exists and is still valid
   */
  getJointCache(
    wallId: string,
    connectedWallIds: string[],
    currentWallPositions: Map<string, { start: THREE.Vector2; end: THREE.Vector2; wallWidth: number }>
  ): WallJointCache | null {
    const positionHash = Array.from(currentWallPositions.values())
      .map(pos => pos ? `${pos.start.x.toFixed(3)},${pos.start.y.toFixed(3)}|${pos.end.x.toFixed(3)},${pos.end.y.toFixed(3)}` : 'invalid')
      .join('|');

    const jointKey = WallJointCacheManager.generateJointKey(wallId, connectedWallIds, positionHash);
    const cached = this.cache.get(jointKey);

    if (!cached) {
      this.cacheMisses++;
      return null;
    }

    // Verify the cache is still valid
    if (this.isCacheValid(cached, currentWallPositions)) {
      this.cacheHits++;
      return cached;
    }

    // Cache is invalid, remove it
    this.cache.delete(jointKey);
    this.cacheMisses++;
    return null;
  }

  /**
   * Check if cached joint is still valid
   */
  private isCacheValid(
    cached: WallJointCache,
    currentPositions: Map<string, { start: THREE.Vector2; end: THREE.Vector2; wallWidth: number }>
  ): boolean {
    // Check if all required walls are present
    for (const [wallId, cachedPos] of cached.wallPositions) {
      const currentPos = currentPositions.get(wallId);
      if (!currentPos) return false;

      // Check if positions match within tolerance
      if (this.positionChanged(cachedPos.start, currentPos.start) ||
          this.positionChanged(cachedPos.end, currentPos.end) ||
          Math.abs(cachedPos.wallWidth - currentPos.wallWidth) > 0.001) {
        return false;
      }
    }

    return true;
  }

  /**
   * Check if position changed significantly
   */
  private positionChanged(pos1: THREE.Vector2, pos2: THREE.Vector2, threshold: number = 0.001): boolean {
    return Math.abs(pos1.x - pos2.x) > threshold || Math.abs(pos1.y - pos2.y) > threshold;
  }

  /**
   * Invalidate cache entries for specific walls
   */
  invalidateCacheForWalls(wallIds: string[]): void {
    const toDelete: string[] = [];

    this.cache.forEach((cache, key) => {
      const shouldDelete = wallIds.some(wallId =>
        cache.dependencies.has(wallId) || key.startsWith(wallId)
      );

      if (shouldDelete) {
        toDelete.push(key);
      }
    });

    toDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Cleanup old cache entries
   */
  private cleanupCache(): void {
    if (this.cache.size <= this.maxCacheSize) return;

    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].lastModified - b[1].lastModified);

    const toDelete = entries.slice(0, Math.floor(this.cache.size * 0.2));

    toDelete.forEach(([key]) => {
      this.cache.delete(key);
    });
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.cacheHits = 0;
    this.cacheMisses = 0;
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { hits: number; misses: number; size: number; hitRate: number } {
    const total = this.cacheHits + this.cacheMisses;
    return {
      hits: this.cacheHits,
      misses: this.cacheMisses,
      size: this.cache.size,
      hitRate: total > 0 ? this.cacheHits / total : 0
    };
  }

  /**
   * Get all cached joint keys
   */
  getCachedJointKeys(): string[] {
    return Array.from(this.cache.keys());
  }
}